import {
  SpaceBetween,
  FormField,
  Input,
  ExpandableSection,
  Checkbox,
  RadioGroup,
  Button,
  Container,
  Header,
  Select,
} from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { EventInput, WaiverRequirement } from '@amzn/red-velvet-api';
import { OptionalLabel } from './Common';
import { useCustomQuestions, isChoiceType } from '../../../../hooks/events/useCustomQuestions';

export interface AdvancedSignupComponentProps {
  eventFields: EventInput;
  updateEventFields: (overrides: Partial<EventInput>) => void;
}

/** Parses an ISO 8601 duration (e.g. "P7D", "P2W", "P3M") into type + numeric value. */
function parseSignupDeadline(
  deadline: string | undefined,
  t: (key: string) => string
): { type: { label: string; value: string }; value: string } {
  if (!deadline) return { type: { label: t('shiftStep.noDeadline'), value: 'none' }, value: '' };
  const match = deadline.match(/^P(\d+)([DWM])$/);
  if (!match) return { type: { label: t('shiftStep.noDeadline'), value: 'none' }, value: '' };
  const num = match[1];
  const unit = match[2];
  const typeMap: Record<string, { labelKey: string; value: string }> = {
    D: { labelKey: 'shiftStep.deadlineDays', value: 'days' },
    W: { labelKey: 'shiftStep.deadlineWeeks', value: 'weeks' },
    M: { labelKey: 'shiftStep.deadlineMonths', value: 'months' },
  };
  const entry = typeMap[unit];
  return { type: { label: t(entry.labelKey), value: entry.value }, value: num };
}

export function AdvancedSignupComponent({
  eventFields,
  updateEventFields,
}: AdvancedSignupComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);

  const [waiverUrl, setWaiverUrl] = useState(eventFields?.waiverUrl ?? '');
  const [waiverRequired, setWaiverRequired] = useState(
    eventFields?.waiverRequired === WaiverRequirement.REQUIRED
  );
  const [hasTShirt, setHasTShirt] = useState(eventFields?.hasTShirt ? 'yes' : 'no');
  const [allowGuests, setAllowGuests] = useState(eventFields?.allowGuests ? 'true' : 'false');

  const [parsedDeadline] = useState(() => parseSignupDeadline(eventFields?.signupDeadline, t));
  const [signupDeadlineType, setSignupDeadlineType] = useState<{ label: string; value: string }>(
    parsedDeadline.type
  );
  const [signupDeadlineValue, setSignupDeadlineValue] = useState(parsedDeadline.value);
  const [deadlineError, setDeadlineError] = useState('');

  const cq = useCustomQuestions(eventFields?.questions, (questions) => {
    updateEventFields({ questions });
  });

  const syncQuestions = () => {
    updateEventFields({ questions: cq.toApiQuestions() });
  };

  return (
    <ExpandableSection
      headerDescription={t('shiftStep.advancedDescription')}
      headerText={t('shiftStep.advancedHeader')}
      variant="stacked"
    >
      <SpaceBetween size="l">
        <FormField
          description={t('shiftStep.waiverUrlDescription')}
          label={<OptionalLabel>{t('shiftStep.waiverUrl')}</OptionalLabel>}
        >
          <Input
            placeholder="https://example.com/waiver"
            type="url"
            value={waiverUrl}
            onChange={({ detail }) => {
              setWaiverUrl(detail.value);
              if (!detail.value.trim()) {
                setWaiverRequired(false);
              }
            }}
            onBlur={() => {
              const trimmed = waiverUrl.trim();
              updateEventFields({
                waiverUrl: trimmed,
                ...(!trimmed && { waiverRequired: WaiverRequirement.NONE }),
              });
            }}
          />
        </FormField>

        {waiverUrl.trim() && (
          <FormField
            description={t('shiftStep.waiverRequiredDescription')}
            label={<OptionalLabel>{t('shiftStep.waiverRequired')}</OptionalLabel>}
            stretch
          >
            <Checkbox
              checked={waiverRequired}
              onChange={({ detail }) => {
                setWaiverRequired(detail.checked);
                updateEventFields({
                  waiverRequired: detail.checked
                    ? WaiverRequirement.REQUIRED
                    : WaiverRequirement.NONE,
                });
              }}
            >
              {t('shiftStep.waiverIsRequired')}
            </Checkbox>
          </FormField>
        )}

        <FormField
          description={t('shiftStep.tShirtDescription')}
          label={<OptionalLabel>{t('shiftStep.tShirt')}</OptionalLabel>}
          stretch
        >
          <RadioGroup
            items={[
              { label: t('misc.yes'), value: 'yes' },
              { label: t('misc.no'), value: 'no' },
            ]}
            value={hasTShirt}
            onChange={({ detail }) => {
              setHasTShirt(detail.value);
              updateEventFields({ hasTShirt: detail.value === 'yes' });
            }}
          />
        </FormField>

        <FormField
          description={t('shiftStep.allowGuestsDescription')}
          label={t('shiftStep.allowGuests')}
          stretch
        >
          <RadioGroup
            items={[
              { label: t('misc.yes'), value: 'true' },
              { label: t('misc.no'), value: 'false' },
            ]}
            value={allowGuests}
            onChange={({ detail }) => {
              setAllowGuests(detail.value);
              updateEventFields({ allowGuests: detail.value === 'true' });
            }}
          />
        </FormField>

        <FormField
          description={t('shiftStep.customQuestionsDescription')}
          label={<OptionalLabel>{t('shiftStep.customQuestions')}</OptionalLabel>}
        >
          <SpaceBetween size="l">
            {cq.items.map((item, index) => {
              const qId = item.question.id ?? '';
              return (
                <Container
                  key={qId}
                  header={
                    <Header
                      actions={
                        <Button variant="link" onClick={() => cq.removeQuestion(qId)}>
                          {t('shiftStep.removeQuestion')}
                        </Button>
                      }
                      variant="h2"
                    >
                      {t('shiftStep.customQuestionHeader', { number: index + 1 })}
                    </Header>
                  }
                >
                  <SpaceBetween size="l">
                    <FormField
                      label={t('shiftStep.questionLabel')}
                      errorText={cq.questionErrors[qId]}
                    >
                      <Input
                        placeholder={t('shiftStep.questionPlaceholder')}
                        value={item.question.title ?? ''}
                        onChange={({ detail }) => {
                          cq.updateTitle(qId, detail.value);
                          if (detail.value.trim()) {
                            cq.clearQuestionError(qId);
                          }
                        }}
                        onBlur={() => {
                          if (!item.question.title?.trim()) {
                            cq.setQuestionError(qId, t('shiftStep.questionTitleRequired'));
                          }
                          syncQuestions();
                        }}
                      />
                    </FormField>

                    <FormField label={t('shiftStep.answerType')}>
                      <Select
                        options={cq.answerTypeOptions}
                        placeholder={t('shiftStep.answerTypePlaceholder')}
                        selectedOption={item.answerTypeOption}
                        onChange={({ detail }) => {
                          cq.updateAnswerType(
                            qId,
                            detail.selectedOption as (typeof cq.answerTypeOptions)[number]
                          );
                        }}
                      />
                    </FormField>

                    {isChoiceType(item.question.answerType) && (
                      <FormField
                        constraintText={t('shiftStep.answerOptionsConstraint')}
                        description={t('shiftStep.answerOptionsDescription')}
                        label={t('shiftStep.answerOptions')}
                        errorText={cq.optionErrors[qId]}
                      >
                        <SpaceBetween size="s">
                          {(item.question.options ?? []).map((option) => {
                            const oId = option.id ?? '';
                            return (
                              <div
                                key={oId}
                                style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}
                              >
                                <div style={{ flex: 1 }}>
                                  <Input
                                    placeholder={t('shiftStep.optionPlaceholder')}
                                    value={option.label ?? ''}
                                    onChange={({ detail }) => {
                                      cq.updateOptionLabel(qId, oId, detail.value);
                                      const allFilled = (item.question.options ?? []).every((o) =>
                                        o.id === option.id ? detail.value.trim() : o.label?.trim()
                                      );
                                      if (allFilled) {
                                        cq.clearOptionError(qId);
                                      }
                                    }}
                                    onBlur={() => {
                                      if (!option.label?.trim()) {
                                        cq.setOptionError(qId, t('shiftStep.optionsNotEmpty'));
                                      }
                                      syncQuestions();
                                    }}
                                  />
                                </div>
                                <Button variant="normal" onClick={() => cq.removeOption(qId, oId)}>
                                  {t('shiftStep.removeOption')}
                                </Button>
                              </div>
                            );
                          })}
                          {(item.question.options ?? []).length < cq.maxOptions && (
                            <Button
                              iconName="add-plus"
                              variant="normal"
                              onClick={() => cq.addOption(qId)}
                            >
                              {t('shiftStep.addOption')}
                            </Button>
                          )}
                        </SpaceBetween>
                      </FormField>
                    )}
                  </SpaceBetween>
                </Container>
              );
            })}
            {cq.canAddQuestion && (
              <Button iconName="add-plus" variant="link" onClick={cq.addQuestion}>
                {t('shiftStep.addCustomQuestion')}
              </Button>
            )}
          </SpaceBetween>
        </FormField>

        <FormField
          description={t('shiftStep.signupDeadlineDescription')}
          label={<OptionalLabel>{t('shiftStep.signupDeadline')}</OptionalLabel>}
          errorText={deadlineError}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ minWidth: '200px' }}>
              <Select
                selectedOption={signupDeadlineType}
                options={[
                  { label: t('shiftStep.noDeadline'), value: 'none' },
                  { label: t('shiftStep.deadlineDays'), value: 'days' },
                  { label: t('shiftStep.deadlineWeeks'), value: 'weeks' },
                  { label: t('shiftStep.deadlineMonths'), value: 'months' },
                ]}
                onChange={({ detail }) => {
                  const selected = detail.selectedOption as { label: string; value: string };
                  setSignupDeadlineType(selected);
                  setSignupDeadlineValue('');
                  setDeadlineError('');
                  updateEventFields({
                    signupDeadline: selected.value === 'none' ? undefined : '',
                  });
                }}
              />
            </div>
            {signupDeadlineType.value !== 'none' && (
              <>
                <div style={{ width: '80px' }}>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={signupDeadlineValue}
                    onChange={({ detail }) => {
                      const max =
                        signupDeadlineType.value === 'days'
                          ? 30
                          : signupDeadlineType.value === 'weeks'
                            ? 52
                            : 12;
                      const parsed = parseInt(detail.value);
                      if (detail.value !== '' && (isNaN(parsed) || parsed < 1 || parsed > max))
                        return;
                      setSignupDeadlineValue(detail.value);
                      if (detail.value.trim()) setDeadlineError('');
                      updateEventFields({
                        signupDeadline: detail.value
                          ? `P${detail.value}${signupDeadlineType.value === 'days' ? 'D' : signupDeadlineType.value === 'weeks' ? 'W' : 'M'}`
                          : '',
                      });
                    }}
                    onBlur={() => {
                      if (!signupDeadlineValue.trim()) {
                        setDeadlineError(
                          t('shiftStep.deadlineEmpty', { selection: signupDeadlineType.label })
                        );
                      }
                    }}
                  />
                </div>
                <span style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>
                  {signupDeadlineType.value === 'days' && t('shiftStep.deadlineDaysLabel')}
                  {signupDeadlineType.value === 'weeks' && t('shiftStep.deadlineWeeksLabel')}
                  {signupDeadlineType.value === 'months' && t('shiftStep.deadlineMonthsLabel')}
                </span>
              </>
            )}
          </div>
        </FormField>
      </SpaceBetween>
    </ExpandableSection>
  );
}
